import React, { useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';

import Container from '@magoosh/layouts/components/container';
import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';
import { formatPrice } from 'utilities/money';

import UnderlinedText from '@marketing/components/underlined_text';
import FAQSection from '@marketing/sections/faq';

import AISection from './sections/ai';
import AppsSection from './sections/apps';
import FeaturesSection from './sections/features';
import ValuePropsSection from './sections/value_props';
import VideoBasedLearningSection from './sections/video_based_learning';
import WallOfLoveSection from './sections/wall_of_love';

import CompetitorComparisonSection from '../sections/competitor_comparison';
import GuaranteeSection from '../sections/guarantee';
import HeroSection from '../sections/hero';
import OfficialQuestionsSection from './sections/official_questions';
import SocialProofSection from '../sections/social_proof';

import marketingStyles from '@marketing/style.module.scss';
import styles from '../style.module.scss';
import toeflStyles from './style.module.scss';

interface Props extends PageProps {
  mapInCents: number;
}

export default (props: Props) => {
  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.purpleGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />

        <Container>
          <div style={{ position: 'absolute', left: 0, top: -25, zIndex: 1 }}>
            <img
              alt="Background image of purple circle radius"
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzOTAiIGhlaWdodD0iNDExIiBmaWxsPSJub25lIj48bWFzayBpZD0iYiIgd2lkdGg9IjQ2MCIgaGVpZ2h0PSI0NTYiIHg9Ii05OCIgeT0iLTE0NiIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgc3R5bGU9Im1hc2stdHlwZTphbHBoYSI+PHBhdGggZmlsbD0idXJsKCNhKSIgZD0iTTIxNi41MTYtMTQ1LjExMmgzMzh2MzQ4aC0zMzh6IiB0cmFuc2Zvcm09InJvdGF0ZSg2NC42MTMgMjE2LjUxNiAtMTQ1LjExMikiLz48L21hc2s+PGcgc3Ryb2tlPSIjQjY3NEZGIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIHN0cm9rZS13aWR0aD0iNjMuNzUiIG1hc2s9InVybCgjYikiIG9wYWNpdHk9Ii4yIj48Y2lyY2xlIGN4PSIxNjEuMDc5IiBjeT0iMTYxLjA3OSIgcj0iMTI5LjIwNCIgb3BhY2l0eT0iLjIiIHRyYW5zZm9ybT0ic2NhbGUoMSAtMSkgcm90YXRlKC0xNS4xMTkgLTYyMC4yNjIgMjc3LjMwNSkiLz48cGF0aCBkPSJNMTc3LjkwNCAxNDQuNDhBMTI5LjIgMTI5LjIgMCAwIDAgMTUwLjQ2LTgxLjc1NmExMjkuMjA0IDEyOS4yMDQgMCAwIDAtOTEuMDE5LS45MjciIG9wYWNpdHk9Ii4yIi8+PHBhdGggZD0iTTIxOS44NjggOTUuMjg1YTEyOS4yMiAxMjkuMjIgMCAwIDAgNS45OTQtOTguNzA2IDEyOS4yMTEgMTI5LjIxMSAwIDAgMC02NS41NTgtNzQuMDM0IiBvcGFjaXR5PSIuMiIvPjwvZz48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImEiIHgxPSI1NTQuNTE2IiB4Mj0iMjE2LjUxNiIgeTE9IjI4Ljg4OCIgeTI9IjI4Ljg4OCIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPjxzdG9wIHN0b3AtY29sb3I9IiNEOUQ5RDkiLz48c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiM3MzczNzMiIHN0b3Atb3BhY2l0eT0iMCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjwvc3ZnPg=="
            />
          </div>

          <div className="hidden-xs hidden-sm" style={{ position: 'absolute', right: -50, zIndex: 1 }}>
            <img
              alt="Background image of purple circle radius"
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1NDQiIGhlaWdodD0iNTUwIiBmaWxsPSJub25lIj48ZyBjbGlwLXBhdGg9InVybCgjYSkiIG9wYWNpdHk9Ii40Ij48bWFzayBpZD0iYyIgd2lkdGg9Ijg5MyIgaGVpZ2h0PSI5NDIiIHg9IjEyOSIgeT0iLTIyOSIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgc3R5bGU9Im1hc2stdHlwZTphbHBoYSI+PHBhdGggZmlsbD0idXJsKCNiKSIgZD0iTTEyOS0yMjloODkzdjk0MkgxMjl6Ii8+PC9tYXNrPjxnIHN0cm9rZT0iI0I2NzRGRiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBzdHJva2Utd2lkdGg9IjYzLjc1IiBtYXNrPSJ1cmwoI2MpIj48Y2lyY2xlIGN4PSI1MzUuMjUiIGN5PSIzMDYuMjUiIHI9IjM2Mi45MTQiIG9wYWNpdHk9Ii4yIiB0cmFuc2Zvcm09InJvdGF0ZSgxMTguNDczIDUzNS4yNSAzMDYuMjUpIi8+PHBhdGggZD0iTTIwMy4zOTkgMTU5LjM0MmEzNjIuOTE1IDM2Mi45MTUgMCAwIDEgNjM4LjM2NS00Ny40MDMgMzYyLjkxNSAzNjIuOTE1IDAgMCAxIDUyLjA1MiAyNTAuMzE4IiBvcGFjaXR5PSIuMiIvPjxwYXRoIGQ9Ik0yNTMuNDg5IDc3LjUyQTM2Mi45MTUgMzYyLjkxNSAwIDAgMSA3NjMuOTggMjQuNDkiIG9wYWNpdHk9Ii4yIi8+PC9nPjwvZz48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImIiIHgxPSIxMjkiIHgyPSIzMjIiIHkxPSIyNDIiIHkyPSIzMjYuNSIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPjxzdG9wIHN0b3AtY29sb3I9IiNEOUQ5RDkiLz48c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiM3MzczNzMiIHN0b3Atb3BhY2l0eT0iMCIvPjwvbGluZWFyR3JhZGllbnQ+PGNsaXBQYXRoIGlkPSJhIj48cGF0aCBmaWxsPSIjZmZmIiBkPSJNMC0yMjloNTQ0djc3OUgweiIvPjwvY2xpcFBhdGg+PC9kZWZzPjwvc3ZnPg=="
            />
          </div>
        </Container>
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 60 }}
        dashedBorder
        examName="TOEFL"
        navigationItems={props.navigationItems}
        navigationStyle={{ background: 'transparent' }}
        notice={props.notice}
      >
        <div style={{ zIndex: 1 }}>
          <HeroSection
            header={<>The smartest way to master the TOEFL<span className={styles.registeredMark}>®</span></>}
            interactivePrompt={{
              answerChoices: (
                <div className={cx(toeflStyles.answerChoicesContainer, 'align-items-center d-flex flex-1')}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" fill="none"><path fill="#5C5C5C" fill-opacity=".15" d="M14 28c4.412 0 8-3.59 8-8V8c0-4.412-3.588-8-8-8S6 3.588 6 8v12c0 4.41 3.588 8 8 8Zm14-8v-4a2 2 0 0 0-4 0v4c0 5.514-4.486 10-10 10S4 25.514 4 20v-4a2 2 0 0 0-4 0v4c0 7.04 5.226 12.864 12 13.84V36H6a2 2 0 1 0 0 4h16a2 2 0 0 0 0-4h-6v-2.16c6.774-.976 12-6.8 12-13.84Z"/></svg>
                </div>
              ),
              prompt: "You are working at an electronics store. Your manager is teaching you how to help customers set up their home Wi-Fi networks. Listen to the manager and repeat what the manager says. Repeat only once.",
              subject: 'Speaking'
            }}
            mapInCents={props.mapInCents}
            subHeader={
              <>
                Self-paced course built entirely
                {' '}<UnderlinedText color="#B674FF">from authentic TOEFL® questions</UnderlinedText>
                {' '}created by ETS, an AI tutor, video lessons, and top-rated mobile apps.
              </>
            }
          />
        </div>

        <SocialProofSection
          quotes={[
            {
              content: <>"The questions are very realistic and the solutions <mark style={{ background: '#FCF0BF' }}>clear every possible doubt</mark>. The graded essays were very insightful and helped me to improve. The <mark style={{ background: '#FCF0BF' }}>amount of practice is more than enough</mark> to exercise."</>,
              scores: [{ name: 'TOEFL Score', score: '5' }],
              studentName: 'Martin C.'
            },
            {
              content: <>"Magoosh not only <mark style={{ background: '#FCF0BF' }}>elevated my score</mark> but ensured I was familiar with the test structure. The detailed breakdown of each section, coupled with <mark style={{ background: '#FCF0BF' }}>realistic practice tests</mark>, equipped me to navigate the TOEFL seamlessly."</>,
              scores: [{ name: 'TOEFL Score', score: '5.5' }],
              studentName: 'Pratheek  B.'
            },
            {
              content: <>"<mark style={{ background: '#FCF0BF' }}>All the videos</mark> transformed the way I see the exam. I started answering the questions using the structure for each section, and <mark style={{ background: '#FCF0BF' }}>my score improved considerably</mark> (+21!). On TOEFL day, I knew exactly what to do."</>,
              scores: [{ name: 'TOEFL Score', score: '5.5' }],
              studentName: 'Carol S.'
            },
            {
              content: <>"The <mark style={{ background: '#FCF0BF' }}>detailed insight</mark> into my potential areas of improvement based on my performance in the practice tests <mark style={{ background: '#FCF0BF' }}>boosted my confidence</mark> on the day of the test. It was a cakewalk, thanks to Magoosh."</>,
              scores: [{ name: 'TOEFL Score', score: '5.5' }],
              studentName: 'Adithi K.'
            }
          ]}
          tagline="Purchased by 75,000+ TOEFL students"
        />

        <div className="d-flex flex-column gap-m">
          <CompetitorComparisonSection
            competitorShortfalls={[
              'Many rely on simulated or third-party questions',
              'No AI support to guide your studying',
              'Text-based explanations only',
              'Not optimized for mobile',
              <>Costs upwards of <strong>$249</strong></>
            ]}
            magooshFeatures={[
              'Every question is an authentic TOEFL® question created by ETS and used under license',
              'Get a Magoosh-trained AI tutor that works with you until you get it',
              'Video and text-based lessons to support multiple learning styles',
              'Download our mobile apps to practice on the go',
              <>Starts at an affordable <strong>{formatPrice(props.mapInCents, 'usd', true)}</strong></>
            ]}
            pointIncrease={4}
            title="Better prep. Better scores. Guaranteed."
           />
          <div className={cx(marketingStyles.dashedBorderBottom, marketingStyles.fullContainerWidth)} style={{ height: 1 }} />
        </div>

        <OfficialQuestionsSection />

        <ValuePropsSection />

        <AISection />

        <AppsSection />

        <VideoBasedLearningSection />

        <WallOfLoveSection />

        <FeaturesSection />

        <div className="d-flex flex-column gap-m">
          <GuaranteeSection pointIncrease={4} />
          <div className={cx(marketingStyles.dashedBorderBottom, marketingStyles.fullContainerWidth)} style={{ height: 1 }} />
        </div>

        <FAQSection
          entries={[
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh, but it's best for individuals who fall into one of the following categories:</p>
                  <ul>
                    <li><b>Self-studiers:</b> those who prefer to study on their own time and at their own pace.</li>
                    <li><b>Video-lovers:</b> those who learn better by watching video than through reading or live-learning. Every practice question has a video or text explanation.</li>
                    <li><b>Busy students and professionals:</b> those who do not have much time to study and want to sneak in a practice session here and there.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: (
                <>
                  If you’re unhappy for any reason, email us within 7 days of the <i>original</i> purchase date (regardless if you pay in installments), and we will give you a full refund. No strings attached. If you purchase Magoosh using a non-US credit card and request a refund, sometimes your credit card company will keep the foreign currency transaction fee.
                </>
              ),
              question: 'How does the refund policy work?'
            },
            {
              answer: "We compare your performance in the program with data from Magoosh students' actual scores and performance in our program and then provide you with a predicted range for the actual test.",
              question: 'How does your score predictor work?'
            },
            {
              answer: (
                <>
                  Check out this <a href="https://magoosh.zendesk.com/hc/en-us/articles/360048879351">help article with a list of accessibility features!</a>
                </>
              ),
              question: 'What accessibility features are there?'
            },
            {
              answer: (
                <>
                  Yes! Check out our <a href="https://ielts.magoosh.com">IELTS website</a> to learn more.
                </>
              ),
              question: 'Does Magoosh also offer IELTS prep?'
            }
          ]}
          footerNote={<>A full list of frequently asked questions and their answers can be found on the <a className="text-body" href="/plans#faq">plans page</a>.</>}
        />
      </PageLayout>
    </div>
  );
};
